import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getFirestore, collection, onSnapshot, QuerySnapshot, DocumentData, Timestamp, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';


type LifePlusDoc = {
  id?: string;
  name?: string;
  email?: string;
  createdAt?: Timestamp | Date;
  input_data?: {
    age?: number;
    gender?: string;
    smoking?: string;
    systolic?: number;
    cholesterol?: number;
  };
  results?: {
    smokeYears?: number;
    systolicYears?: number;
    cholesterolYears?: number;
  };
  interventions?: Array<{ name?: string; years?: number; type?: string }>;
  total_years?: number;
  resultIdentifier?: string;
  resultIdentifierDisplay?: string;
  success?: boolean;
};

const AdminPage: React.FC = () => {
  const { user, signOut } = useAuth();
  const [rows, setRows] = useState<LifePlusDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Only allow specific email to delete all entries
  const ALLOWED_DELETE_EMAIL = 'chenmor69@gmail.com'; // Change this to the allowed email
  const canDeleteAll = user?.email === ALLOWED_DELETE_EMAIL;
  // Handler for remove all button
  const handleRemoveAllClick = () => {
    setShowConfirm(true);
  };
  React.useEffect(() => {
    const signOutAsync = async () => {
      const auth = getAuth();
      if(auth.currentUser?.email === null || auth.currentUser?.email === undefined) {
        await signOut();
      }
      
    };
    signOutAsync();
  }, []);
  // Handler for confirm dialog
  const handleConfirmDelete = async () => {
    setDeleting(true);
    setShowConfirm(false);
    setDeleteError(null);
    try {
      const db = getFirestore();
      const colRef = collection(db, 'life-plus-results');
      const snapshot = await getDocs(colRef);
      const deletePromises = snapshot.docs.map((d) => deleteDoc(doc(db, 'life-plus-results', d.id)));
      await Promise.all(deletePromises);
    } catch (err) {
      setDeleteError('שגיאה במחיקת הרשומות');
      console.error('Error deleting all records:', err);
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowConfirm(false);
  };
  useEffect(() => {
    const db = getFirestore();
    const colRef = collection(db, 'life-plus-results');

    const unsubscribe = onSnapshot(
      colRef,
      (snap: QuerySnapshot<DocumentData>) => {
        const data = snap.docs.map(d => {
          const docData = d.data() as DocumentData;
          // Get timestamp from document (createdAt field or use document ID timestamp as fallback)
          let createdAt: Timestamp | Date | undefined = docData.createdAt;

          // If createdAt is not in the document, try to extract from document ID or use current time
          // Note: For better accuracy, createdAt should be added when creating documents
          if (!createdAt) {
            // Try to use document ID as timestamp (if it's a timestamp-based ID)
            // Otherwise, use current time (not accurate but better than nothing)
            createdAt = Timestamp.now();
          }

          // Convert to Timestamp if it's a Date or other format
          if (createdAt instanceof Date) {
            createdAt = Timestamp.fromDate(createdAt);
          }

          return {
            id: d.id,
            ...docData,
            createdAt: createdAt as Timestamp
          } as LifePlusDoc;
        });

        // Sort by creation date (newest first)
        const sortedData = data.sort((a, b) => {
          const aTime = a.createdAt instanceof Timestamp ? a.createdAt.toMillis() :
            a.createdAt instanceof Date ? a.createdAt.getTime() : 0;
          const bTime = b.createdAt instanceof Timestamp ? b.createdAt.toMillis() :
            b.createdAt instanceof Date ? b.createdAt.getTime() : 0;
          return bTime - aTime;
        });

        setRows(sortedData);
        setLoading(false);
      },
      (err) => {
        console.error('Firestore snapshot error', err);
        setError(err.message || 'Firestore error');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (e) {
      console.error('Sign out failed', e);
      alert('שגיאה בהתנתקות');
    }
  };

  // Format date for display
  const formatDate = (timestamp: Timestamp | Date | undefined): string => {
    if (!timestamp) return '-';
    const date = timestamp instanceof Timestamp ? timestamp.toDate() : timestamp;
    return date.toLocaleDateString('he-IL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Helper function for Hebrew year pluralization
  const getYearText = (years: number): string => {
    if (years === 0) return '';
    if (years === 1) return 'שנה';
    if (years === 2) return 'שנתיים';
    return `${years} שנים`;
  };

  // Helper function for Hebrew month pluralization
  const getMonthText = (months: number): { number: string; text: string } => {
    if (months === 0) return { number: '', text: '' };
    if (months === 1) return { number: 'חודש אחד', text: '' };
    if (months === 2) return { number: 'חודשיים', text: '' };
    return { number: `${months}`, text: ' חודשים' };
  };

  // Format years in Hebrew format (like ResultsPage)
  const formatYearsHebrew = (value: number | undefined): string => {
    if (typeof value !== 'number' || isNaN(value)) return '-';
    
    const totalYears = Math.floor(value);
    const months = Math.round((value % 1) * 12);
    const yearText = getYearText(totalYears);
    
    if (totalYears === 0 && months === 0) {
      return '0 שנים';
    }
    
    if (months > 0) {
      const monthData = getMonthText(months);
      const monthText = monthData.number + monthData.text;
      if (yearText) {
        // For 2 months (חודשיים), don't use dash after ו
        const connector = months === 2 ? 'ו' : 'ו-';
        return `${yearText} ${connector}${monthText}`;
      } else {
        return monthText;
      }
    }
    return yearText;
  };

  // Format number to one decimal place (kept for backward compatibility if needed)
  const formatYears = (value: number | undefined): string => {
    if (typeof value !== 'number') return '-';
    return value.toFixed(1);
  };

  // Generate identifier (LP001, LP002, etc.)
  const generateIdentifier = (index: number): string => {
    const num = index + 1;
    return `LP${num.toString().padStart(3, '0')}`;
  };

  // Translate values to Hebrew
  const translateToHebrew = (value: string | undefined): string => {
    if (!value) return '-';
    const lowerValue = value.toLowerCase();
    if (lowerValue === 'male' || lowerValue === 'גבר') return 'גבר';
    if (lowerValue === 'female' || lowerValue === 'אשה') return 'אשה';
    if (lowerValue === 'yes' || lowerValue === 'כן') return 'כן';
    if (lowerValue === 'no' || lowerValue === 'לא') return 'לא';
    return value;
  };

  useEffect(() => {
    document.title = 'עמוד ניהול - חיים פלוס';
  }, []);
  if (error) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        width: '100vw',
        background: '#fff',
        color: '#d32f2f',
        fontSize: 32,
        fontWeight: 700,
        textAlign: 'center',
        zIndex: 9999
      }}>
        You are not authorized to view this page
      </div>
    );
  }
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        width: '100vw',
        background: '#fff'
      }}>
        <div style={{
          border: '6px solid #f3f3f3',
          borderTop: '6px solid #3498db',
          borderRadius: '50%',
          width: 48,
          height: 48,
          animation: 'spin 1s linear infinite',
          marginBottom: 16
        }} />
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg);}
              100% { transform: rotate(360deg);}
            }
          `}
        </style>
        {/* <div>טוען...</div> */}
      </div>
    );
  }
  return (
    <div style={{ padding: 24, height: '100vh', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
      <a href="#main-content" className="skip-link">דלג לתוכן הראשי</a>
      {deleteError && (
        <div style={{ color: '#d32f2f', background: '#fff3f3', border: '1px solid #f8d7da', padding: '12px 20px', borderRadius: 4, marginBottom: 16, fontSize: 16, textAlign: 'center' }}>
          {deleteError}
        </div>
      )}
      <div style={{ marginBottom: 16, paddingLeft: 20, paddingRight: 20, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
          <p style={{ margin: 0 }}>ברוך הבא, {user?.displayName || user?.email}</p>
          {canDeleteAll && (
            <button onClick={handleRemoveAllClick} style={{ marginRight: 'auto', marginLeft: 16, background: '#e53935', color: 'white', border: 'none', borderRadius: 4, padding: '6px 16px', cursor: 'pointer', fontWeight: 600 }} disabled={deleting || loading} aria-busy={deleting} aria-disabled={deleting || loading}>
              {deleting ? 'מוחק...' : 'מחק את כל הרשומות'}
            </button>
          )}
          <button onClick={handleSignOut} style={{ marginRight: '16px', background: 'none', border: 'none', color: 'blue', cursor: 'pointer', padding: 0, fontSize: 'inherit', fontFamily: "'Open Sans', sans-serif" }} aria-label="התנתק מהמערכת">התנתק</button>

        </div>
        {/* Confirm dialog */}
        {showConfirm && (
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-dialog-title"
            aria-describedby="confirm-dialog-description"
            style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.3)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={handleCancelDelete}
          >
            <div
              style={{ background: 'white', padding: 32, borderRadius: 8, boxShadow: '0 2px 16px rgba(0,0,0,0.2)', minWidth: 320, textAlign: 'center' }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 id="confirm-dialog-title" style={{ margin: '0 0 24px 0', fontSize: 18 }}>מחיקת כל הרשומות</h2>
              <div id="confirm-dialog-description" style={{ marginBottom: 24, fontSize: 18 }}>האם אתה בטוח שברצונך למחוק את כל הרשומות? פעולה זו אינה הפיכה.</div>
              <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
                <button onClick={handleConfirmDelete} style={{ background: '#e53935', color: 'white', border: 'none', borderRadius: 4, padding: '8px 24px', fontWeight: 600 }}>מחק</button>
                <button onClick={handleCancelDelete} style={{ background: '#eee', color: '#333', border: 'none', borderRadius: 4, padding: '8px 24px', fontWeight: 600 }}>בטל</button>
              </div>
            </div>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0 }}>סיכום תוצאות מחשבון הוספת חיים</h2>
          {!loading && !error && rows.length > 0 && (
            <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>סה"כ רשומות: {rows.length}</p>
          )}
        </div>
      </div>

      <main id="main-content" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {loading && <div role="status" aria-live="polite">טוען נתונים...</div>}
        {!loading && !error && (
          <div style={{ flex: 1, paddingLeft: 20, paddingRight: 20, direction: 'rtl', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div style={{ flex: 1, overflowY: 'auto', overflowX: 'auto', minHeight: 0 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', direction: 'rtl', fontSize: '14px' }}>
                <thead style={{ position: 'sticky', top: 0, backgroundColor: '#F7FBFB', zIndex: 10 }}>
                  <tr>
                    <th scope="col" style={{ borderBottom: '1px solid #ddd', padding: 8, backgroundColor: '#F7FBFB' }}>מזהה</th>
                    <th scope="col" style={{ borderBottom: '1px solid #ddd', padding: 8, backgroundColor: '#F7FBFB' }}>תאריך</th>
                    <th scope="col" style={{ borderBottom: '1px solid #ddd', padding: 8, backgroundColor: '#F7FBFB' }}>גיל</th>
                    <th scope="col" style={{ borderBottom: '1px solid #ddd', padding: 8, backgroundColor: '#F7FBFB' }}>מין</th>
                    <th scope="col" style={{ borderBottom: '1px solid #ddd', padding: 8, backgroundColor: '#F7FBFB' }}>לחץ דם סיסטולי</th>
                    <th scope="col" style={{ borderBottom: '1px solid #ddd', padding: 8, backgroundColor: '#F7FBFB' }}>כולסטרול</th>
                    <th scope="col" style={{ borderBottom: '1px solid #ddd', padding: 8, backgroundColor: '#F7FBFB' }}>עישון</th>
                    <th scope="col" style={{ borderBottom: '1px solid #ddd', padding: 8, backgroundColor: '#F7FBFB' }}>שנות לחץ דם סיסטולי</th>
                    <th scope="col" style={{ borderBottom: '1px solid #ddd', padding: 8, backgroundColor: '#F7FBFB' }}>שנות כולסטרול</th>
                    <th scope="col" style={{ borderBottom: '1px solid #ddd', padding: 8, backgroundColor: '#F7FBFB' }}>שנות עישון</th>
                    <th scope="col" style={{ borderBottom: '1px solid #ddd', padding: 8, backgroundColor: '#F7FBFB' }}>סה"כ שנים</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, index) => {
                    const cholesterolYears = r.results?.cholesterolYears ?? r.interventions?.find(i => i.type === 'cholesterol')?.years;
                    const systolicYears = r.results?.systolicYears ?? r.interventions?.find(i => i.type === 'systolic')?.years;
                    const smokeYears = r.results?.smokeYears ?? r.interventions?.find(i => i.type === 'smoking')?.years;

                    return (
                      <tr key={r.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: 8 }}>{r.resultIdentifierDisplay}</td>
                        <td style={{ padding: 8 }}>{formatDate(r.createdAt)}</td>
                        <td style={{ padding: 8 }}>{r.input_data?.age ?? '-'}</td>
                        <td style={{ padding: 8 }}>{translateToHebrew(r.input_data?.gender)}</td>
                        <td style={{ padding: 8 }}>{r.input_data?.systolic ?? '-'}</td>
                        <td style={{ padding: 8 }}>{r.input_data?.cholesterol ?? '-'}</td>
                        <td style={{ padding: 8 }}>{translateToHebrew(r.input_data?.smoking)}</td>
                        <td style={{ padding: 8 }}>{formatYearsHebrew(systolicYears)}</td>
                        <td style={{ padding: 8 }}>{formatYearsHebrew(cholesterolYears)}</td>
                        <td style={{ padding: 8 }}>{formatYearsHebrew(smokeYears)}</td>
                        <td style={{ padding: 8 }}>{formatYearsHebrew(r.total_years)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {rows.length === 0 && <div role="status" style={{ marginTop: 12 }}>לא נמצאו רשומות</div>}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminPage;